import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { css } from "dx-styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api, itemDeleteRefusal } from "../api/client";
import type { ItemDeleteRefusal, ItemDraft, LibraryItem } from "../api/types";
import {
  Modal,
  button,
  card,
  errorText,
  heading,
  layout,
  muted,
  row,
  spread,
  stack,
} from "../components/primitives";
import { tokens } from "../theme/tokens";

const controls = css({ display: "flex", gap: tokens.space.sm, alignItems: "center", flexWrap: "wrap" });

const field = css({
  minHeight: "36px",
  padding: `0 ${tokens.space.sm}`,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
});

const search = css({ minWidth: "220px", flex: "1 1 220px" });

const chip = css({
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
  borderRadius: "999px",
  padding: `2px ${tokens.space.sm}`,
  fontSize: "12px",
  cursor: "pointer",
});

const chipOn = css({ backgroundColor: tokens.color.olive, color: tokens.color.paper, borderColor: tokens.color.olive });

const tagList = css({ display: "flex", gap: tokens.space.xs, flexWrap: "wrap" });

// The scroll container is the virtualizer's viewport; its height has to be fixed
// for windowing to mean anything.
const scroller = css({
  height: "min(60vh, 640px)",
  overflowY: "auto",
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.md,
  backgroundColor: tokens.color.paper,
});

const grid = css({
  display: "grid",
  gridTemplateColumns: "minmax(220px, 2fr) 90px 90px minmax(160px, 1.5fr) 120px 90px",
  gap: tokens.space.sm,
  alignItems: "center",
  padding: `${tokens.space.sm} ${tokens.space.md}`,
  borderBottom: `1px solid ${tokens.color.rule}`,
});

const headerRow = css({
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: tokens.color.kraft,
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: tokens.color.pencil,
});

const numeric = css({ textAlign: "end" });

const rowHeight = 56;

interface Props {
  readonly onSignedOut: () => void;
}

export function Library({ onSignedOut }: Props) {
  const [items, setItems] = useState<readonly LibraryItem[] | null>(null);
  const [query, setQuery] = useState("");
  const [activeTags, setActiveTags] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<LibraryItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [refusal, setRefusal] = useState<ItemDeleteRefusal | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const reload = useCallback(
    async (search: string) => {
      try {
        setItems(await api.items(search));
        setError(null);
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 401) {
          onSignedOut();
          return;
        }
        setError(caught instanceof Error ? caught.message : "Could not load the library.");
      }
    },
    [onSignedOut],
  );

  // Debounced: typing in a search box should not be one request per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => void reload(query), 200);
    return () => window.clearTimeout(timer);
  }, [query, reload]);

  const allTags = useMemo(() => {
    const seen = new Set<string>();
    for (const item of items ?? []) for (const tag of item.tags) seen.add(tag);
    return [...seen].sort();
  }, [items]);

  const visible = useMemo(() => {
    if (activeTags.length === 0) return items ?? [];
    return (items ?? []).filter((item) => activeTags.every((tag) => item.tags.includes(tag)));
  }, [items, activeTags]);

  const columns = useMemo<ColumnDef<LibraryItem>[]>(
    () => [
      {
        id: "title",
        header: "Item",
        cell: ({ row: { original } }) => (
          <div className={stack}>
            <strong>{original.title}</strong>
            {original.notes ? <span className={muted}>{original.notes}</span> : null}
          </div>
        ),
      },
      { id: "kind", header: "Kind", cell: ({ row: { original } }) => <span className={muted}>{original.kind}</span> },
      {
        id: "weight",
        header: "Weight",
        cell: ({ row: { original } }) => (
          <span className={numeric}>
            {original.defaultWeight == null ? "—" : `${original.defaultWeight} kg`}
          </span>
        ),
      },
      {
        id: "tags",
        header: "Tags",
        cell: ({ row: { original } }) => (
          <div className={tagList}>
            {original.tags.length === 0 ? <span className={muted}>—</span> : null}
            {original.tags.map((tag) => (
              <span key={tag} className={chip}>
                {tag}
              </span>
            ))}
          </div>
        ),
      },
      {
        id: "usedIn",
        header: "Used in",
        cell: ({ row: { original } }) => (
          <span className={muted}>
            {original.referencingCollections === 0
              ? "—"
              : `${original.referencingCollections} collection${original.referencingCollections === 1 ? "" : "s"}`}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row: { original } }) => (
          <button type="button" className={button({ size: "sm" })} onClick={() => setEditing(original)}>
            Edit
          </button>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({ data: visible as LibraryItem[], columns, getCoreRowModel: getCoreRowModel() });
  const rows = table.getRowModel().rows;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  const save = useCallback(
    async (draft: ItemDraft, existing: LibraryItem | null) => {
      try {
        if (existing) {
          await api.updateItem(existing.id, {
            title: draft.title,
            kind: draft.kind,
            defaultWeight: draft.defaultWeight ?? null,
            tags: draft.tags ?? [],
            notes: draft.notes ?? null,
          });
        } else {
          await api.createItem(draft);
        }
        setEditing(null);
        setCreating(false);
        await reload(query);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "That item could not be saved.");
      }
    },
    [query, reload],
  );

  const remove = useCallback(
    async (item: LibraryItem) => {
      try {
        await api.deleteItem(item.id);
        setEditing(null);
        await reload(query);
      } catch (caught) {
        const refused = itemDeleteRefusal(caught);
        if (refused) {
          // The whole point of the 409: show which rules to fix. The edit dialog
          // has to close first — two modals open at once means the refusal is
          // rendered underneath the form that triggered it.
          setEditing(null);
          setRefusal(refused);
          return;
        }
        setError(caught instanceof Error ? caught.message : "That item could not be deleted.");
      }
    },
    [query, reload],
  );

  return (
    <main className={layout}>
      <div className={spread}>
        <div className={stack}>
          <h1 className={heading}>Gear library</h1>
          <p className={muted}>
            Items belong to you, not to a collection — the same rope keeps its identity everywhere it is used.
          </p>
        </div>
        <button type="button" className={button({ tone: "primary" })} onClick={() => setCreating(true)}>
          New item
        </button>
      </div>

      {error ? <p className={errorText}>{error}</p> : null}

      <div className={controls}>
        <input
          className={`${field} ${search}`}
          placeholder="Search titles and tags"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search the library"
        />
        <div className={tagList}>
          {allTags.map((tag) => {
            const on = activeTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                className={on ? `${chip} ${chipOn}` : chip}
                aria-pressed={on}
                onClick={() =>
                  setActiveTags((current) => (on ? current.filter((t) => t !== tag) : [...current, tag]))
                }
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      <p className={muted}>
        {items === null
          ? "Loading…"
          : `${visible.length} of ${items.length} item${items.length === 1 ? "" : "s"}`}
      </p>

      <div className={scroller} ref={scrollRef}>
        <div className={`${grid} ${headerRow}`} role="row">
          {table.getHeaderGroups()[0]?.headers.map((header) => (
            <span key={header.id} role="columnheader">
              {flexRender(header.column.columnDef.header, header.getContext())}
            </span>
          ))}
        </div>
        {/*
          Inline styles, deliberately: these are per-row pixel offsets that change
          on every scroll frame. dx-styles extracts static rules, so anything
          genuinely dynamic belongs in the style attribute (or a CSS variable) —
          not in a css() call, which would not compile.
        */}
        <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const tableRow = rows[virtualRow.index];
            if (!tableRow) return null;
            return (
              <div
                key={tableRow.id}
                className={grid}
                role="row"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {tableRow.getVisibleCells().map((cell) => (
                  <div key={cell.id} role="cell">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <p className={muted}>
        New items get a stable id derived from their title, so a script and the editor converge on the same
        item instead of creating twins. Imported items keep the id they arrive with.
      </p>

      <ItemForm
        open={creating || editing !== null}
        item={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSave={save}
        onDelete={remove}
      />

      <Modal
        open={refusal !== null}
        onOpenChange={(open) => {
          if (!open) setRefusal(null);
        }}
        title="This item is still in use"
        description={refusal?.reason}
      >
        <div className={stack}>
          {(refusal?.references ?? []).map((reference) => (
            <div key={`${reference.collectionId}-${reference.templateTitle}`} className={card}>
              <strong>{reference.templateTitle}</strong>
              <div className={muted}>in {reference.collectionName}</div>
            </div>
          ))}
          <button type="button" className={button({ tone: "primary" })} onClick={() => setRefusal(null)}>
            Got it
          </button>
        </div>
      </Modal>
    </main>
  );
}

function ItemForm({
  open,
  item,
  onClose,
  onSave,
  onDelete,
}: {
  readonly open: boolean;
  readonly item: LibraryItem | null;
  readonly onClose: () => void;
  readonly onSave: (draft: ItemDraft, existing: LibraryItem | null) => Promise<void>;
  readonly onDelete: (item: LibraryItem) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<LibraryItem["kind"]>("gear");
  const [weight, setWeight] = useState("");
  const [tags, setTags] = useState("");
  const [notes, setNotes] = useState("");

  // Reset when the dialog opens on a different item.
  useEffect(() => {
    if (!open) return;
    setTitle(item?.title ?? "");
    setKind(item?.kind ?? "gear");
    setWeight(item?.defaultWeight == null ? "" : String(item.defaultWeight));
    setTags(item?.tags.join(", ") ?? "");
    setNotes(item?.notes ?? "");
  }, [open, item]);

  const parsedWeight = weight.trim() === "" ? null : Number(weight);
  const weightIsBad = parsedWeight !== null && (Number.isNaN(parsedWeight) || parsedWeight < 0);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={item ? "Edit item" : "New item"}
      description={
        item
          ? "Renaming keeps the same id: it is already in published bundles and in subscribers' libraries."
          : "The id comes from the title, so creating the same item twice converges instead of duplicating."
      }
    >
      <form
        className={stack}
        onSubmit={(event) => {
          event.preventDefault();
          if (weightIsBad) return;
          void onSave(
            {
              title,
              kind,
              defaultWeight: kind === "gear" ? parsedWeight : null,
              tags: tags
                .split(",")
                .map((tag) => tag.trim())
                .filter((tag) => tag.length > 0),
              notes: notes.trim() === "" ? null : notes,
            },
            item,
          );
        }}
      >
        <label className={stack}>
          <span className={muted}>Title</span>
          <input className={field} value={title} onChange={(event) => setTitle(event.target.value)} required />
        </label>
        <label className={stack}>
          <span className={muted}>Kind</span>
          <select
            className={field}
            value={kind}
            onChange={(event) => setKind(event.target.value as LibraryItem["kind"])}
          >
            <option value="gear">gear</option>
            <option value="task">task</option>
            <option value="purchase">purchase</option>
          </select>
        </label>
        {/* Only gear carries weight — a task with a weight is a data error. */}
        {kind === "gear" ? (
          <label className={stack}>
            <span className={muted}>Weight (kg)</span>
            <input
              className={field}
              value={weight}
              inputMode="decimal"
              onChange={(event) => setWeight(event.target.value)}
              aria-invalid={weightIsBad}
            />
            {weightIsBad ? <span className={errorText}>A weight has to be zero or more.</span> : null}
          </label>
        ) : null}
        <label className={stack}>
          <span className={muted}>Tags (comma separated)</span>
          <input className={field} value={tags} onChange={(event) => setTags(event.target.value)} />
          <span className={muted}>Stored lower-cased and de-duplicated, the way group rules match them.</span>
        </label>
        <label className={stack}>
          <span className={muted}>Notes</span>
          <input className={field} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        <div className={row}>
          <button type="submit" className={button({ tone: "primary" })} disabled={title.trim() === "" || weightIsBad}>
            Save
          </button>
          <button type="button" className={button()} onClick={onClose}>
            Cancel
          </button>
          {item ? (
            <button type="button" className={button({ tone: "danger" })} onClick={() => void onDelete(item)}>
              Delete
            </button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
